// components/ChecklistFlow.jsx
import { useState, useEffect, useRef } from "react";
import { Button } from './ui/button.jsx';
import { Textarea } from './ui/textarea.jsx';
import { Input } from './ui/input.jsx';
import confetti from "canvas-confetti";
import { Card, CardContent } from './ui/card.jsx';
import { useAuth } from "../AuthContext";


export default function ChecklistFlow({ inventory }) {
  // ──────────────────────
  // 1) Hooks only, up front
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [showResponse, setShowResponse] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [lastResponseIndex, setLastResponseIndex] = useState(-1);
  const [fadeOut, setFadeOut] = useState(false);
  const [showAreaSplash, setShowAreaSplash] = useState(true);
  const [previousArea, setPreviousArea] = useState(null);
  const [splashTimeout, setSplashTimeout] = useState(null);
  const [randomColor, setRandomColor] = useState("#f1c40f");
  const grouped = groupAndSortInventory(inventory);
  const allItems = grouped.flatMap(g => g.items);
  const currentItem = allItems[currentIndex];
  const { user } = useAuth();
  const submissionId = useRef(crypto.randomUUID());
  const submissionStarted = useRef(false);
  const [submissionMessage, setSubmissionMessage] = useState('Saving your checklist…');
  const [submissionSaved, setSubmissionSaved] = useState(false);
  const [submissionError, setSubmissionError] = useState(false);
  const [retrySubmission, setRetrySubmission] = useState(0);

  // console.log("✅ currentItem =", currentItem);

  const currentGroup = grouped.find((g) => g.items.includes(currentItem));

  const funColors = [
    "#3498db", "#e74c3c", "#9b59b6",
    "#e67e22", "#1abc9c", "#680ece", "#459cbc"
  ];

  // Reset whenever inventory changes
  useEffect(() => {
    setCurrentIndex(0);
    setResponses({});
    submissionId.current = crypto.randomUUID();
    submissionStarted.current = false;
    setSubmissionSaved(false);
    setSubmissionError(false);
  }, [inventory]);

  useEffect(() => {
    if (showResponse) {
      const fadeTimer = setTimeout(() => {
        setFadeOut(true);
      }, 2300);

      const transitionTimer = setTimeout(() => {
        setShowResponse(false);
        setFadeOut(false);
      }, 3600);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(transitionTimer);
      };
    }
  }, [showResponse]);
  useEffect(() => {
    if (!showResponse && currentGroup?.area !== previousArea) {
      setShowAreaSplash(true);
      setPreviousArea(currentGroup?.area);

      const timeout = setTimeout(() => {
        setShowAreaSplash(false);
      }, 5000); // show splash for 5 seconds

      setSplashTimeout(timeout);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, currentGroup?.area]);

  useEffect(() => {
    if (!currentItem && submissionSaved) {
      const logoutTimer = setTimeout(() => {
        fetch("/api/logout", {
          method: "POST",
          credentials: "include"
        }).then(() => {
          console.log("👋 Auto-logged out after completion");
        });
      }, 300000); // Allow time to review the saved Sysco result.

      return () => clearTimeout(logoutTimer);
    }
  }, [currentItem, submissionSaved]);


  // ✅ Place this just before: if (!currentItem) return ( ... )

  useEffect(() => {
    const sendEmail = async () => {
      try {
        const res = await fetch("/api/email-vendor-lists", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          credentials: "include",
          body: JSON.stringify({
            responses,
            inventory,
            submissionId: submissionId.current,
            userEmail: "bonadaas1@hotmail.com", // 👈 Fixed address
            storeId: user?.store || "unknown"
          })

        });

        const result = await res.json();
        if (result.ok) {
          setSubmissionSaved(true);
          setSubmissionError(false);
          setSubmissionMessage(result.emailStatus === 'sent'
            ? 'Checklist saved. The shopping list and Sysco results have been emailed.'
            : result.emailStatus === 'needs_review'
              ? 'Checklist saved. Email delivery needs checking; view the Sysco preparation results.'
              : 'Checklist saved. The shopping list will be emailed when Sysco preparation finishes, including anything that needs checking.');
        } else {
          setSubmissionError(true);
          submissionStarted.current = false;
          setSubmissionMessage(result.error || 'Could not save your checklist. Please retry.');
        }
      } catch (err) {
        setSubmissionError(true);
        submissionStarted.current = false;
        setSubmissionMessage('Connection interrupted. Retry to check and save this checklist.');
      }
    };

    const checklistIsComplete =
      currentItem === undefined &&
      inventory?.length > 0 &&
      Object.keys(responses).length === inventory.length;

    if (checklistIsComplete && !submissionStarted.current) {
      submissionStarted.current = true;
      setSubmissionError(false);
      setSubmissionMessage('Saving your checklist…');
      sendEmail();
    }

  }, [currentItem, retrySubmission]);
  // ──────────────────────
  // 2) Early return _after_ hooks
  if (!inventory || inventory.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-xl">Loading inventory…</p>
      </div>
    );
  }

  // ──────────────────────
  // 3) Now you can compute derived values

  // …and the rest of your logic…

  const getRandomColor = () => {
    const randomIndex = Math.floor(Math.random() * funColors.length);
    return funColors[randomIndex];
  };


  const handleInput = (value) => {
    if (!currentItem) return;

    setResponses((prev) => ({ ...prev, [currentItem.id]: value }));

    if ((currentIndex + 1) % 7 === 0) {
      const { message, index } = getRandomEncouragement(lastResponseIndex);
      setResponseMessage(message);
      setLastResponseIndex(index);
      setRandomColor(getRandomColor());
      setShowResponse(true);
    }

    const nextIndex = currentIndex + 1;

    if (nextIndex === allItems.length) {
      launchCrazyVictoryConfetti();
      launchConfetti(true); // double whammy
      setTimeout(() => {
        setCurrentIndex(nextIndex);
      }, 2000);
      return;
    }


    // 🎉 Confetti at every 10th item milestone
    if (nextIndex % 10 === 0) {
      launchConfetti(false); // milestone confetti
    }

    // 👣 Always move to next item after answering
    setTimeout(() => {
      setCurrentIndex(nextIndex);
    }, 1000); // matches your encouragement message timing
  };


  const prevItem = () => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  if (!currentItem) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gradient-to-b from-[#459cbc] to-[#680ece] text-white space-y-6">
        <div className="text-5xl font-extrabold text-white animate-bounce">
          🎉 Inventory Complete! 🎉
        </div>
        <div className="text-xl text-center">Great job, {user?.email || "team"}!</div>
        <div className="text-md text-center" role="status">{submissionMessage}</div>
        {submissionError && <Button onClick={() => setRetrySubmission(n => n + 1)}>Retry saving checklist</Button>}
        {submissionSaved && <a className="underline text-lg" href="/api/sysco/review">View Sysco preparation</a>}
        <div className="text-3xl animate-wiggle mt-4">💥💥💥</div>
      </div>
    );
  }


  function launchCrazyVictoryConfetti() {
    confetti({
      origin: { y: 0.6 },
      colors: ["#459cbc", "#680ece", "#2ecc71", "#e74c3c", "#f1c40f"],
      particleCount: 500,
      spread: 200,
      scalar: 1.2,
      startVelocity: 70,
    });
  }


  const renderAnswerInput = () => {
    const value = responses[currentItem?.id] || "";

    if (currentItem?.predefinedOptions?.length) {
      return (
        <div className="flex flex-col space-y-4">
          <select
            className="border p-2 w-full rounded-md text-black"
            value={value}
            onChange={(e) => handleInput(e.target.value)}
          >
            <option value="" disabled>Select an option</option>
            {currentItem.predefinedOptions.map((opt, index) => (
              <option key={index} value={opt}>{opt}</option>
            ))}
          </select>

          {/* Confirm Button */}
          {currentItem && responses[currentItem.id] !== undefined && responses[currentItem.id] !== "" && !showResponse && (
            <div className="flex justify-center pt-2">
              <Button onClick={() => setCurrentIndex((i) => i + 1)} className="bg-green-500 hover:bg-green-600">
                Confirm and Continue
              </Button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col space-y-4">
        <Input
          type="text"
          placeholder="Type your answer..."
          value={value}
          onChange={(e) => handleInput(e.target.value)}
        />

        {/* Confirm Button */}
        {currentItem && responses[currentItem.id] !== undefined && responses[currentItem.id] !== "" && !showResponse && (
          <div className="flex justify-center pt-2">
            <Button onClick={() => setCurrentIndex((i) => i + 1)} className="bg-green-500 hover:bg-green-600">
              Confirm and Continue
            </Button>
          </div>
        )}
      </div>
    );
  };


  if (showResponse) {
    const animations = [
      { class: "animate-wiggle", emoji: "💃" },
      { class: "animate-fade-in", emoji: "🌅" },
      { class: "animate-zoom-in", emoji: "🔍" },
      { class: "animate-flip", emoji: "🔄" },
      { class: "animate-slide-in", emoji: "🛹" },
      { class: "animate-shake", emoji: "🪩" },
      { class: "animate-bounce-in", emoji: "🏀" },
      { class: "animate-pop", emoji: "🎈" },
      { class: "animate-pulse-grow", emoji: "💓" },
      { class: "animate-spin-in", emoji: "🌀" },
      { class: "animate-drop-in", emoji: "🪂" },
    ];

    const selected = animations[Math.floor(Math.random() * animations.length)];

    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <div className={`text-5xl ${selected.class}`}>{selected.emoji}</div>
        <div
          className={`text-5xl md:text-6xl font-extrabold text-center px-4 ${selected.class} animate-pop animate-pulse-grow transition-all duration-700 ${fadeOut ? "opacity-0 transition-opacity duration-1000" : "opacity-100"
            }`}
          style={{
            color: randomColor,
            textShadow: "2px 2px 8px rgba(0,0,0,0.5)",
          }}
        >
          {responseMessage}
        </div>

      </div>
    );
  }
  if (showAreaSplash) {
    // 🚪 Show "Entering Freezer" or similar splash
    return (
      <div className="flex items-center justify-center h-screen bg-brandBlue text-white">
        <div className="text-3xl font-extrabold animate-fade-in">
          🚪 Lets Take a Walk Over To The: {currentGroup?.area}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6 p-4 bg-gradient-to-b from-[#459cbc] to-[#680ece] min-h-screen text-white">
      <div className="text-2xl font-bold text-center py-4 bg-brandBlue text-white rounded-lg shadow mb-2">
        🚪 We Are In The: {currentGroup.area}
      </div>
      <div
        className={`text-5xl md:text-6xl font-extrabold text-center px-4 animate-pop animate-pulse-grow transition-all duration-700 ${fadeOut ? "opacity-0 transition-opacity duration-1000" : "opacity-100"
          }`}
        style={{
          color: "#ffffff",
          textShadow: `0 0 8px ${randomColor}, 0 0 16px ${randomColor}`,
        }}
      >
        {currentItem.promptText}
      </div>

      <div className="bg-black bg-opacity-10 p-4 rounded-lg shadow-md">
        {renderAnswerInput()}
        <Textarea
          placeholder="Add notes (optional)"
          value={currentItem.notes || ""}
          disabled
          className="mt-4 text-black"
        />
        <div className="flex justify-center pt-4">
          <Button onClick={prevItem} disabled={currentIndex === 0} className="bg-[#e74c3c] hover:bg-[#c0392b]">
            ← Back
          </Button>


        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.round(((currentIndex + 1) / allItems.length) * 100)}%`,
              background: "linear-gradient(to right, #459cbc, #680ece)",
            }}
          ></div>
        </div>

      </div>
    </div>
  );

  function groupAndSortInventory(items) {
    return Object.entries(
      items
        .filter((item) => item.promptText)
        .reduce((acc, item) => {
          const area = item.storageArea || "Other";
          if (!acc[area]) acc[area] = [];
          acc[area].push(item);
          return acc;
        }, {})
    ).map(([area, items]) => ({
      area,
      items: items.sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999)),
    }));
  }
  function launchConfetti(isFinal = false) {
    const defaults = {
      origin: { y: 0.6 },
      colors: ["#680ece", "#459cbc"], // brand colors
    };

    if (isFinal) {
      // 🎊 Final massive celebration
      confetti({
        ...defaults,
        particleCount: 300,
        spread: 160,
        startVelocity: 60,
      });
    } else {
      // 🎈 Smaller milestone burst
      confetti({
        ...defaults,
        particleCount: 100,
        spread: 90,
        startVelocity: 45,
      });
    }
  }

  function getRandomEncouragement(lastIndex = -1) {
    const messages = [
      "Inventory gods are smiling on you 😎.",
      "🔥 One more box closer to victory.",
      "You just unlocked level 72 of stock wizardry.",
      "📦 Boom. Counted. Logged. Dominated.",
      "Stay focused, you're crushing it!",
      "Every count matters!",
      "You’re making this place run like a machine!",
      "Look at you go!",
      "One step closer to done!",
      "Crushing it like a pro!!!",
      "Okay, calm down—save some inventory for the rest of us.",
      "🚀 You’re on a mission and failure is allergic to you.",
      "You just made that item wish it could high five you.",
      "👏 Not all heroes wear aprons.",
      "Inventory ninja mode: ON",
      "🔥 That’s what we call focus!",
      "Checking like a champion!",
      "Your future self says thanks!",
      "Numbers don’t lie — and neither do your skills!",
      "👏 Top-tier stuff right there!",
      "If excellence was illegal, you'd be on the run.",
      "👏👏👏 That deserves a slow clap.",
      "This kitchen runs on YOU."
    ];

    let index;
    do {
      index = Math.floor(Math.random() * messages.length);
    } while (index === lastIndex);

    return { message: messages[index], index };
  }
}